import { AppBar, createStyles, Grid, List, Paper, WithStyles, withStyles } from '@material-ui/core';
import { Form, Formik, FormikActions, FormikProps } from 'formik';
import _ from 'lodash';
import { inject } from 'mobx-react';
import * as React from 'react';
import { RouteComponentProps } from 'react-router';
import { DefaultBehavior, DefaultStep, IBehavior, IChevron, IStep } from 'src/Definitions';
import { BehaviorDto, StepDto } from 'src/generated_client';
import { IStores } from 'src/Stores';
import * as Yup from 'yup';
import Button from '../common/Button';
import { ExtendedBehaviorDto } from './behaviors/ExtendedBehaviorDto';
import './Chevrons.css';
import { ExtendedStepDto } from './steps/ExtendedStepDto';
import StepEditor from './steps/StepEditor';

const styles = createStyles({
    chevronBar: {
        background: 'transparent',
        boxShadow: 'none',
    },
    gridContainer: {
        paddingBottom: '2%',
        paddingTop: '2%',
    },
    pageContainer: {
        padding: '3%',
    },
    paper: {
        marginLeft: '5px',
        marginRight: '5px',
        marginTop: '30px',
        padding: '5px',
        width: '100%',
    },
    stepList: {
        width: '100%',
    },
});

interface IChevronFormValues {
    chevrons: IChevron[];
}

interface IMatchParams {
    assessmentId: string;
}

const ChevronSchema = Yup.object().shape({
    branchKpi: Yup.object(),
});

interface IChevronEditorProps extends WithStyles<typeof styles>, RouteComponentProps<IMatchParams> {
    chevrons?: IChevron[];
    assessmentChevrons?: IChevron[];
    steps?: IStep[];
    behaviours?: IBehavior[];
    getChevrons?: (pageNumber?: number, pageSize?: number) => Promise<void>;
    getChevronsFromAssessment?: (assessmentId: string) => Promise<void>;
    getCurrentPath?: string;
    saveBehavior?: (assessmentId: string, behavior: IBehavior) => Promise<void>;
    saveChevron?: (chevron: IChevron) => Promise<void>;
    saveStep?: (assessmentId: string, step: IStep) => Promise<void>;
    isAdmin?: boolean;
    isLoading?: boolean;
    sendNotification?: (key: string, message: string) => void;
    removeStep?: (assessmentId: string, stepId: string) => Promise<void>;
    removeBehavior?: (assessmentId: string, stepId: string) => Promise<void>;
    rerouteToPath?: (path: string) => void;
}

class ChevronEditor extends React.Component<IChevronEditorProps> {
    public componentDidMount() {
        const currentPath = this.props.getCurrentPath && this.props.getCurrentPath!.split('/')[1];

        if (this.state.assessmentId) {
            this.props.getChevronsFromAssessment!(this.state.assessmentId);
        } else {
            if (this.props.isAdmin) {
                if (currentPath === 'chevroneditor') {
                    this.props.rerouteToPath!('/adminchevroneditor');
                } else {
                    this.props.getChevrons!(undefined, 5000);
                }
            } else {
                this.props.rerouteToPath!('/assessments');
            }
        }
    }

    public handleClick = (value: number) => (event: React.MouseEvent) => {
        this.setState({ value });
        event.preventDefault();
    };

    public state = {
        assessmentId: this.props.match.params.assessmentId,
        value: 0,
    };

    public save = (values: IChevronFormValues, formikBag: FormikActions<IChevronFormValues>) => {
        if (this.state.assessmentId) {
            this.saveToAssessment(values.chevrons, formikBag);
        } else {
            values.chevrons.forEach((chevron) => {
                chevron.steps = this.removeMarkedSteps(chevron.steps as ExtendedStepDto[]);
                this.props.saveChevron!(chevron).finally(() => {
                    formikBag.setSubmitting(false);
                });
            });
            this.props.sendNotification!('200', 'Updated Default Chevrons');
        }
    };

    public removeMarkedSteps(steps: ExtendedStepDto[]) {
        steps.forEach((step) => {
            if (step.isRemoved) {
                _.remove(steps, step);
            } else {
                step.behaviors = this.removeMarkedBehaviors(
                    step.behaviors as ExtendedBehaviorDto[]
                );
            }
        });
        return steps;
    }

    public removeMarkedBehaviors(behaviors: ExtendedBehaviorDto[]) {
        behaviors.forEach((behavior) => {
            if (behavior.isRemoved) {
                _.remove(behaviors, behavior);
            }
        });
        return behaviors;
    }

    public saveToAssessment = (
        chevrons: IChevron[],
        formikBag: FormikActions<IChevronFormValues>
    ) => {
        chevrons.forEach((chevron) => {
            chevron.steps.forEach((step) => {
                this.saveStepToAssessment(step).then(() => {
                    step.behaviors!.forEach((behavior) => {
                        this.saveBehaviorToAssessment(behavior, step as ExtendedStepDto);
                    });
                });
            });
        });
        this.props.sendNotification!('200', 'Updated Assessment Chevrons');
        formikBag.setSubmitting(false);
    };

    public async saveStepToAssessment(step: StepDto) {
        const stepExtended = step as ExtendedStepDto;
        const stepToSave = {
            ...DefaultStep(),
            ...step,
            isNew: stepExtended.isNew,
        } as IStep;
        if (stepExtended.isEdited) {
            if (stepExtended.isRemoved && !stepExtended.isNew) {
                return this.props.removeStep!(this.state.assessmentId, stepToSave.id).finally(
                    () => {
                        this.props.getChevronsFromAssessment!(this.state.assessmentId);
                    }
                );
            } else if (!stepExtended.isRemoved) {
                return this.props.saveStep!(this.state.assessmentId, stepToSave).finally(() => {
                    this.props.getChevronsFromAssessment!(this.state.assessmentId);
                });
            }
        }
    }

    public saveBehaviorToAssessment(behavior: BehaviorDto, step: ExtendedStepDto) {
        const behaviorExtended = behavior as ExtendedBehaviorDto;
        const behaviorToSave = {
            ...DefaultBehavior(),
            ...behavior,
            isNew: behaviorExtended.isNew,
        } as IBehavior;
        if (!step.isRemoved && behaviorExtended.isEdited) {
            if (behaviorExtended.isRemoved && !behaviorExtended.isNew) {
                this.props.removeBehavior!(this.state.assessmentId, behaviorToSave.id).finally(
                    () => {
                        this.props.getChevronsFromAssessment!(this.state.assessmentId);
                    }
                );
            } else if (!behaviorExtended.isRemoved) {
                this.props.saveBehavior!(this.state.assessmentId, behaviorToSave).finally(() => {
                    this.props.getChevronsFromAssessment!(this.state.assessmentId);
                });
            }
        }
    }

    public addStep = (
        chevron: IChevron,
        stepId: string,
        stepName?: string,
        displayOrder?: number
    ) => {
        if (displayOrder && stepId) {
            const step = new ExtendedStepDto({
                ...DefaultStep(),
                chevronId: chevron.id,
                displayOrder,
                isEdited: true,
                isNew: true,
                name: stepName,
            });

            let steps = _.orderBy(chevron.steps, ['displayOrder'], ['asc']);
            steps.splice(displayOrder - 1, 0, step);
            steps = this.setDisplayOrderToListIndex(steps as ExtendedStepDto[]);
            chevron.steps = steps;
            return chevron;
        }
        return chevron;
    };

    public removeStep = (chevron: IChevron, stepId: string) => {
        const removedStepExtended = _.remove(
            chevron.steps,
            (step) => step.id === stepId
        )[0] as ExtendedStepDto;
        removedStepExtended.isRemoved = true;
        removedStepExtended.isEdited = true;
        chevron.steps.push(removedStepExtended);
        chevron.steps = this.setDisplayOrderToListIndex(chevron.steps as ExtendedStepDto[]);
        return chevron;
    };

    public changeStepName(chevron: IChevron, stepId: string, stepName?: string) {
        const removedStepExtended = _.remove(
            chevron.steps,
            (s) => stepId === s.id
        )[0] as ExtendedStepDto;
        if (removedStepExtended) {
            removedStepExtended.isEdited = true;
            removedStepExtended.name = stepName;
            chevron.steps.push(removedStepExtended);
        }
        return chevron;
    }

    public setDisplayOrderToListIndex = (steps: ExtendedStepDto[]) => {
        let counter = 1;
        steps.forEach((step) => {
            if (!step.isRemoved) {
                if (step.displayOrder !== counter) {
                    step.isEdited = true;
                    step.displayOrder = counter++;
                }
                counter++;
            }
        });
        return steps;
    };

    public changeDisplayOrder = (
        chevron: IChevron,
        stepId: string,
        stepName?: string,
        displayOrder?: number
    ) => {
        if (displayOrder && stepName !== undefined) {
            let steps = chevron.steps;
            steps = _.orderBy(steps, ['displayOrder'], ['asc']);
            const step = _.remove(steps, (s) => stepId === s.id)[0] as ExtendedStepDto;

            step.isEdited = true;

            steps.splice(displayOrder - 1, 0, step);

            steps = this.setDisplayOrderToListIndex(steps as ExtendedStepDto[]);

            chevron.steps = steps;
            return chevron;
        }
        return chevron;
    };

    public skipStep = (chevron: IChevron, stepId: string) => {
        const removedStepExtended = _.remove(
            chevron.steps,
            (s) => s.id === stepId
        )[0] as ExtendedStepDto;
        removedStepExtended.skipped = !removedStepExtended.skipped;
        removedStepExtended.isEdited = true;
        chevron.steps.push(removedStepExtended);
        return chevron;
    };

    public editStep = (
        chevrons: IChevron[],
        setFieldValue: (name: string, value: any) => void,
        callbackEditStep: (
            chevron: IChevron,
            stepId: string,
            stepName?: string,
            displayOrder?: number
        ) => IChevron
    ) => (stepId: string, chevronId: string, stepName?: string, displayOrder?: number) => {
        const chevron = this.getChevron(chevrons, chevronId);
        callbackEditStep(chevron!, stepId, stepName, displayOrder);
        this.updateFormikChevrons(chevrons, setFieldValue, chevron!);
    };

    public updateFormikChevrons(
        chevrons: IChevron[],
        setFieldValue: (name: string, value: any) => void,
        chevronToAdd: IChevron
    ) {
        _.remove(chevrons, (chevron) => chevron.id === chevronToAdd.id);
        chevrons.push(chevronToAdd);
        setFieldValue('chevrons', chevrons);
    }

    public getChevron(chevrons: IChevron[], chevronId: string) {
        return _.find(chevrons, (chevron) => chevron.id === chevronId);
    }

    public updateStepWithBehaviors = (
        chevrons: IChevron[],
        setFieldValue: (name: string, value: any) => void
    ) => (step: StepDto) => {
        const removedChevron = _.remove(chevrons, (c) => c.id === step.chevronId)[0];
        _.remove(removedChevron.steps, (s) => s.id === step.id);
        removedChevron.steps.push(step);
        this.updateFormikChevrons(chevrons, setFieldValue, removedChevron);
    };

    public sortSteps(chevron: IChevron) {
        chevron.steps = _.orderBy(chevron.steps, ['displayOrder'], ['asc']);
        return chevron;
    }

    public isStepShown(step: StepDto) {
        const stepExtended = step as ExtendedStepDto;
        return !stepExtended.isRemoved;
    }

    public render() {
        const { classes, isLoading } = this.props;
        const initialValues = {
            chevrons: this.state.assessmentId ? this.props.assessmentChevrons : this.props.chevrons,
        };
        return (
            <>
                <Formik
                    initialValues={initialValues}
                    onSubmit={this.save}
                    validationSchema={ChevronSchema}
                    enableReinitialize
                >
                    {(formikProps: FormikProps<any>) => (
                        <Form>
                            <Grid
                                className={classes.pageContainer}
                                container
                                direction="column"
                                spacing={24}
                            >
                                {this.renderChevrons(formikProps.values.chevrons)}
                                {this.renderSteps(
                                    this.state.value,
                                    formikProps.values.chevrons,
                                    formikProps.setFieldValue
                                )}
                                <Grid item xs={12}>
                                    <Grid container justify="center">
                                        <Button
                                            disabled={isLoading}
                                            isLoading={isLoading || formikProps.isSubmitting}
                                            color="primary"
                                            type="submit"
                                            variant="contained"
                                        >
                                            Save
                                        </Button>
                                    </Grid>
                                </Grid>
                            </Grid>
                        </Form>
                    )}
                </Formik>
            </>
        );
    }

    private renderChevrons(chevrons: IChevron[]) {
        chevrons = _.orderBy(chevrons, ['displayOrder'], ['asc']);
        return (
            <Grid item xs={12}>
                <AppBar color="inherit" className={this.props.classes.chevronBar}>
                    <Grid container justify="center">
                        <ul className="chevron">
                            {chevrons &&
                                chevrons.map((chevron) => (
                                    <li
                                        key={chevron.id}
                                        style={{
                                            position: 'relative',
                                            zIndex: chevrons.length - (chevron.displayOrder - 1),
                                        }}
                                    >
                                        <a
                                            className={
                                                this.state.value === chevron.displayOrder - 1
                                                    ? 'selectedChevron'
                                                    : ''
                                            }
                                            href="#"
                                            onClick={this.handleClick(chevron.displayOrder - 1)}
                                        >
                                            {chevron.name}
                                        </a>
                                    </li>
                                ))}
                        </ul>
                    </Grid>
                </AppBar>
            </Grid>
        );
    }

    private renderSteps(
        value: number,
        chevrons: IChevron[],
        setFieldValue: (name: string, value: any) => void
    ) {
        chevrons = _.orderBy(chevrons, ['displayOrder'], ['asc']);
        return (
            <>
                {chevrons &&
                    chevrons.map((chevron, index) => (
                        <Paper
                            key={`paper ${chevron.id}`}
                            className={value === index ? this.props.classes.paper : ''}
                        >
                            {value === index && (
                                <Grid key={`grid item ${chevron.id}`} item xs={12}>
                                    <Grid
                                        key={`grid container ${chevron.id}`}
                                        container
                                        justify="center"
                                    >
                                        <List
                                            key={`list item ${chevron.id}`}
                                            className={this.props.classes.stepList}
                                        >
                                            {this.sortSteps(chevron).steps.map(
                                                (step) =>
                                                    this.isStepShown(step) && (
                                                        <StepEditor
                                                            assessmentId={this.state.assessmentId}
                                                            key={step.id}
                                                            step={step}
                                                            removeStep={this.editStep(
                                                                chevrons,
                                                                setFieldValue,
                                                                this.removeStep
                                                            )}
                                                            changeStepName={this.editStep(
                                                                chevrons,
                                                                setFieldValue,
                                                                this.changeStepName
                                                            )}
                                                            changeDisplayOrder={this.editStep(
                                                                chevrons,
                                                                setFieldValue,
                                                                this.changeDisplayOrder
                                                            )}
                                                            addStep={this.editStep(
                                                                chevrons,
                                                                setFieldValue,
                                                                this.addStep
                                                            )}
                                                            skipStep={this.editStep(
                                                                chevrons,
                                                                setFieldValue,
                                                                this.skipStep
                                                            )}
                                                            numberOfSteps={chevron.steps.length}
                                                            updateStepWithBehaviors={this.updateStepWithBehaviors(
                                                                chevrons,
                                                                setFieldValue
                                                            )}
                                                        />
                                                    )
                                            )}
                                        </List>
                                    </Grid>
                                </Grid>
                            )}
                        </Paper>
                    ))}
            </>
        );
    }
}

const InjectedChevronEditor = inject<
    IStores,
    IChevronEditorProps,
    Partial<IChevronEditorProps>,
    any
>((stores: IStores) => ({
    assessmentChevrons: stores.assessments.chevrons,
    chevrons: stores.chevrons.chevrons,
    getChevrons: stores.chevrons.getAllChevrons,
    getChevronsFromAssessment: stores.assessments.getAssessmentProcessById,
    getCurrentPath: stores.global.getCurrentPath,
    isAdmin: stores.global.isAdmin,
    isLoading: stores.chevrons.isLoading,
    removeBehavior: stores.assessments.removeBehavior,
    removeStep: stores.assessments.removeStep,
    rerouteToPath: stores.global.rerouteToPath,
    saveBehavior: stores.assessments.saveBehavior,
    saveChevron: stores.chevrons.saveChevron,
    saveStep: stores.assessments.saveStep,
    sendNotification: stores.chevrons.sendNotification,
}))(withStyles(styles)(ChevronEditor));

export default InjectedChevronEditor;



// WEBPACK FOOTER //
// ./src/chevrons/ChevronEditor.tsx